import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { AUTH } from "@/constants/testIds";
import { toast } from "sonner";

export default function AuthDialog({ open, onOpenChange, initialTab = "register" }) {
  const [tab, setTab] = useState(initialTab);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const { login, register } = useAuth();

  useEffect(() => { if (open) { setTab(initialTab); setError(""); } }, [open, initialTab]);

  async function handleSubmit(e) {
    e.preventDefault();
    setBusy(true); setError("");
    const res = tab === "login"
      ? await login(email, password)
      : await register(name, email, password);
    setBusy(false);
    if (res.ok) {
      toast.success(tab === "login" ? "Welcome back." : "Your family archive begins.");
      onOpenChange(false);
      setEmail(""); setPassword(""); setName("");
    } else {
      setError(res.error);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={AUTH.dialog}
        className="bg-ivory border-ivory-300 max-w-md p-0 overflow-hidden"
      >
        <div className="p-8 pb-6">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl tracking-tight text-ink">
              {tab === "login" ? "Welcome home" : "Begin your archive"}
            </DialogTitle>
            <DialogDescription className="text-ink-50 mt-1">
              {tab === "login"
                ? "Sign in to continue your family's story."
                : "Create a free account and start preserving your legacy."}
            </DialogDescription>
          </DialogHeader>

          <div className="mt-6 inline-flex rounded-full border border-ivory-300 p-1 bg-white/60">
            <button
              type="button"
              data-testid={AUTH.tabRegister}
              onClick={() => { setTab("register"); setError(""); }}
              className={`px-4 py-1.5 text-sm rounded-full transition-colors ${tab === "register" ? "bg-tulsi text-ivory" : "text-ink-50"}`}
            >
              Register
            </button>
            <button
              type="button"
              data-testid={AUTH.tabLogin}
              onClick={() => { setTab("login"); setError(""); }}
              className={`px-4 py-1.5 text-sm rounded-full transition-colors ${tab === "login" ? "bg-tulsi text-ivory" : "text-ink-50"}`}
            >
              Sign in
            </button>
          </div>

          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            {tab === "register" && (
              <div>
                <Label htmlFor="name" className="text-xs uppercase tracking-[0.2em] text-ink-50">Name</Label>
                <Input
                  id="name"
                  data-testid={AUTH.name}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  placeholder="Ananya Iyer"
                  className="mt-2 h-12 bg-transparent border-ivory-300 focus-visible:ring-tulsi"
                />
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.2em] text-ink-50">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid={AUTH.email}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@family.in"
                className="mt-2 h-12 bg-transparent border-ivory-300 focus-visible:ring-tulsi"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.2em] text-ink-50">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid={AUTH.password}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                placeholder="At least 6 characters"
                className="mt-2 h-12 bg-transparent border-ivory-300 focus-visible:ring-tulsi"
              />
            </div>

            {error && (
              <p data-testid={AUTH.error} className="text-sm text-rust">{error}</p>
            )}

            <button
              type="submit"
              disabled={busy}
              data-testid={tab === "login" ? AUTH.submitLogin : AUTH.submitRegister}
              className="w-full h-12 rounded-full bg-saffron text-ivory font-medium tracking-wide hover:bg-saffron-deep active:scale-[0.98] transition disabled:opacity-60"
            >
              {busy ? "Please wait…" : tab === "login" ? "Sign in" : "Create archive"}
            </button>
          </form>
        </div>
        <div className="bg-ivory-200 px-8 py-4 text-xs text-ink-50">
          By continuing you agree to preserve your family's story with care. Part of the SocialPulse network.
        </div>
      </DialogContent>
    </Dialog>
  );
}
